import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  Metric,
  Panel,
  SegmentedControl,
  Stepper,
  Toggle,
  cn,
  PRESS,
  formatDate,
  formatMoney,
  useToast,
  type BadgeTone,
} from 'escrowbuild-ui';

import { usePublications, useT, type AppTranslationKey } from '@/i18n';
import {
  findProject,
  participationsOf,
  type Publication,
  type PublicationStatus,
} from '@/data/domain';

/** The document's §11 walk, in the order it is walked. */
const FLOW: PublicationStatus[] = ['draft', 'in-review', 'approved', 'published'];

/**
 * The four positions, as KEYS rather than as sentences.
 *
 * This map is built before any provider exists, so a label written here would
 * be frozen in whatever language the module happened to load in and would never
 * follow the header's selector. The labelled state travels as a key and the card
 * translates it; the gate sentence travels the same way for the same reason.
 */
const STATE: Record<
  PublicationStatus,
  { labelKey: AppTranslationKey; tone: BadgeTone; bar: string; gateKey: AppTranslationKey }
> = {
  draft: {
    labelKey: 'status.publication.draft',
    tone: 'neutral',
    bar: 'bg-line-strong',
    gateKey: 'publications.gate.draft',
  },
  'in-review': {
    labelKey: 'status.publication.inReview',
    tone: 'warning',
    bar: 'bg-warning',
    gateKey: 'publications.gate.inReview',
  },
  approved: {
    labelKey: 'status.publication.approved',
    tone: 'info',
    bar: 'bg-info',
    gateKey: 'publications.gate.approved',
  },
  published: {
    labelKey: 'status.publication.published',
    tone: 'success',
    bar: 'bg-success',
    gateKey: 'publications.gate.published',
  },
};

/** The demo is signed in as the project owner, so that is who approves. */
const REVIEWER = 'R. Campos';

const today = () => new Date().toISOString().slice(0, 10);

const LABEL = 'font-display text-2xs font-semibold uppercase tracking-label text-muted';

const LINK = cn(
  'font-medium text-heading underline decoration-line-strong underline-offset-2',
  'transition-colors duration-[var(--dur-fast)] hover:decoration-brand',
  PRESS,
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
);

/**
 * The publishing queue (§11), across every development.
 *
 * The screen exists to make one rule legible: a record is not visible to an
 * investor because it was written, only because it was released. So each card
 * carries its position in the four-step walk, the names and dates of everyone
 * who moved it, and the gate that position represents — and the release itself
 * is the one action behind a confirmation, because it is the only irreversible
 * one.
 */
export function PublicationsPage() {
  const { toast } = useToast();
  const t = useT();
  /*
     The queue is resolved per locale, and the demo's own decisions ride ON TOP of
     it as a patch map rather than as a copy of the records.

     A copy taken once into state would freeze every title and period in whatever
     language the screen happened to open in, and a reader who switched to
     Portuguese mid-review would keep reading English cards. Only the fields a
     decision actually changes are held here, and none of them is a word.
  */
  const queue = usePublications();
  const [decisions, setDecisions] = useState<Record<string, Partial<Publication>>>({});
  const records = useMemo(
    () => queue.map((record) => ({ ...record, ...decisions[record.id] })),
    [queue, decisions],
  );
  const [filter, setFilter] = useState('all');
  const [confirming, setConfirming] = useState<Publication | null>(null);

  const countOf = (status: PublicationStatus) =>
    records.filter((record) => record.status === status).length;

  // Work in progress first: a reviewer's queue is what still needs deciding.
  const visible = records
    .filter((record) => filter === 'all' || record.status === filter)
    .sort(
      (a, b) =>
        FLOW.indexOf(a.status) - FLOW.indexOf(b.status) ||
        (b.approvedAt ?? b.period).localeCompare(a.approvedAt ?? a.period),
    );

  function patch(id: string, changes: Partial<Publication>) {
    setDecisions((current) => ({ ...current, [id]: { ...current[id], ...changes } }));
  }

  function submitForReview(record: Publication) {
    patch(record.id, { status: 'in-review' });
    toast({
      variant: 'info',
      title: t('publications.toast.sentTitle'),
      description: t('publications.toast.sentBody', { title: record.title }),
    });
  }

  function approve(record: Publication) {
    patch(record.id, {
      status: 'approved',
      reviewedBy: record.reviewedBy ?? REVIEWER,
      approvedBy: REVIEWER,
      approvedAt: today(),
    });
    toast({
      variant: 'success',
      title: t('status.publication.approved'),
      description: t('publications.toast.approvedBody', { title: record.title }),
    });
  }

  function requestChanges(record: Publication) {
    const toDraft = record.status === 'in-review';
    // A change invalidates an approval — §11 will not let an edited record keep
    // a signature it was given before the edit.
    patch(record.id, {
      status: toDraft ? 'draft' : 'in-review',
      reviewedBy: null,
      approvedBy: null,
      approvedAt: null,
    });
    toast({
      variant: 'warning',
      title: toDraft
        ? t('publications.toast.backToDraftTitle')
        : t('publications.toast.withdrawnTitle'),
      description: toDraft
        ? t('publications.toast.backToDraftBody', { title: record.title })
        : t('publications.toast.withdrawnBody', { title: record.title }),
    });
  }

  function publish(record: Publication) {
    const audience = participationsOf(record.projectId).length;
    const project = findProject(record.projectId);
    patch(record.id, { status: 'published', publishedAt: today() });
    toast({
      variant: record.notify ? 'success' : 'info',
      title: record.notify
        ? t('publications.toast.publishedNotifiedTitle')
        : t('publications.toast.publishedQuietlyTitle'),
      description: record.notify
        ? t('publications.toast.notifiedBody', {
            count: audience,
            project: project?.name ?? '',
          })
        : t('publications.toast.quietBody', { count: audience }),
    });
  }

  const publishedCount = countOf('published');

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('publications.title')}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted">{t('publications.intro')}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {FLOW.map((status) => (
          <Metric
            key={status}
            label={t(STATE[status].labelKey)}
            value={countOf(status)}
            format="number"
            tone={
              status === 'published'
                ? 'success'
                : status === 'approved'
                  ? 'info'
                  : status === 'in-review'
                    ? 'warning'
                    : 'neutral'
            }
            hint={t(STATE[status].gateKey)}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className={cn(LABEL, 'mb-1.5 block')}>{t('publications.queue')}</span>
          <SegmentedControl
            label={t('publications.filterLabel')}
            options={[
              { value: 'all', label: t('publications.filterAll', { count: records.length }) },
              ...FLOW.map((status) => ({
                value: status,
                label: t('publications.filterStatus', {
                  status: t(STATE[status].labelKey),
                  count: countOf(status),
                }),
              })),
            ]}
            value={filter}
            onValueChange={setFilter}
          />
        </div>
        <p className="text-xs text-muted">
          {t('publications.visibleSummary', {
            visible: publishedCount,
            total: records.length,
          })}
        </p>
      </div>

      {visible.length === 0 ? (
        <Panel>
          <EmptyState
            variant="cards"
            title={t('publications.emptyTitle')}
            description={t('publications.emptyBody')}
            filtersActive={filter !== 'all'}
            onClearFilters={() => setFilter('all')}
          />
        </Panel>
      ) : (
        <ul className="flex list-none flex-col gap-5 p-0">
          {visible.map((record) => {
            const project = findProject(record.projectId);
            const meta = STATE[record.status];
            const audience = participationsOf(record.projectId).length;
            const step = FLOW.indexOf(record.status);

            return (
              <li key={record.id}>
                <Panel
                  headerActions={
                    <div className="flex flex-wrap items-center gap-2">
                      {record.status === 'published' ? (
                        <Badge
                          tone={record.notify ? 'success' : 'neutral'}
                          variant="subtle"
                          size="sm"
                        >
                          {record.notify
                            ? t('publications.badgeNotified')
                            : t('publications.badgeQuiet')}
                        </Badge>
                      ) : null}
                      <Badge tone={meta.tone} variant="solid" size="sm">
                        {t(meta.labelKey)}
                      </Badge>
                    </div>
                  }
                  title={
                    <span className="flex items-start gap-2.5">
                      {/* A shape as well as a tone, so the state survives forced colours. */}
                      <span
                        aria-hidden="true"
                        className={cn('mt-1 block h-4 w-1.5 shrink-0 rounded-pill', meta.bar)}
                      />
                      {record.title}
                    </span>
                  }
                  description={
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <Link to={`/projects/${record.projectId}/publications`} className={LINK}>
                        {project?.name ?? record.projectId}
                      </Link>
                      <span aria-hidden="true">·</span>
                      <span>{record.period}</span>
                      <span aria-hidden="true">·</span>
                      <span>{t(meta.gateKey)}</span>
                    </span>
                  }
                  footer={
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-2xs text-muted">
                        {t('publications.reaches', { count: audience })}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        {record.status === 'draft' ? (
                          <Button size="sm" onClick={() => submitForReview(record)}>
                            {t('publications.sendForReview')}
                          </Button>
                        ) : null}

                        {record.status === 'in-review' ? (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => requestChanges(record)}>
                              {t('publications.requestChanges')}
                            </Button>
                            <Button size="sm" onClick={() => approve(record)}>
                              {t('publications.approve')}
                            </Button>
                          </>
                        ) : null}

                        {record.status === 'approved' ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => requestChanges(record)}
                            >
                              {t('publications.withdrawApproval')}
                            </Button>
                            <Button size="sm" onClick={() => setConfirming(record)}>
                              {t('publications.publishToInvestors')}
                            </Button>
                          </>
                        ) : null}

                        {record.status === 'published' ? (
                          <span className="text-2xs text-muted">
                            {t('publications.releasedNote', {
                              date: formatDate(record.publishedAt),
                            })}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  }
                >
                  <Stepper
                    steps={[
                      { label: t('publications.stepPrepared'), description: record.preparedBy },
                      {
                        label: t('publications.stepReviewed'),
                        description: record.reviewedBy ?? t('publications.awaitingReviewer'),
                        status: record.reviewedBy ? 'complete' : undefined,
                      },
                      {
                        label: t('status.publication.approved'),
                        description: record.approvedBy
                          ? `${record.approvedBy} · ${formatDate(record.approvedAt)}`
                          : t('publications.awaitingApproval'),
                      },
                      {
                        label: t('status.publication.published'),
                        description: record.publishedAt
                          ? formatDate(record.publishedAt)
                          : t('publications.notReleased'),
                      },
                    ]}
                    currentStep={step}
                    label={t('publications.progressLabel', { title: record.title })}
                  />

                  <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-line-subtle pt-4 sm:grid-cols-4">
                    <div>
                      <dt className={LABEL}>{t('publications.photographs')}</dt>
                      <dd className="mt-0.5 font-mono text-sm text-heading">{record.photos}</dd>
                    </div>
                    <div>
                      <dt className={LABEL}>{t('publications.documents')}</dt>
                      <dd className="mt-0.5 font-mono text-sm text-heading">{record.documents}</dd>
                    </div>
                    <div>
                      <dt className={LABEL}>{t('publications.spendToDate')}</dt>
                      <dd className="mt-0.5 font-mono text-sm text-heading">
                        {formatMoney(record.spendToDate)}
                      </dd>
                    </div>
                    <div>
                      <dt className={LABEL}>{t('publications.varianceVsBudget')}</dt>
                      <dd
                        className={cn(
                          'mt-0.5 font-mono text-sm',
                          record.variance <= 0 ? 'text-success' : 'text-danger',
                        )}
                      >
                        {formatMoney(record.variance)}
                        <span className="ml-1.5 text-2xs text-muted">
                          {record.variance <= 0
                            ? t('publications.under')
                            : t('publications.over')}
                        </span>
                      </dd>
                    </div>
                  </dl>

                  <p className="mt-4 text-sm text-body">{record.body}</p>

                  <div className="mt-4 border-t border-line-subtle pt-4">
                    <Toggle
                      label={t('publications.notifyLabel')}
                      checked={record.notify}
                      disabled={record.status === 'published'}
                      hint={
                        record.status === 'published'
                          ? record.notify
                            ? t('publications.hintNotified')
                            : t('publications.hintQuiet')
                          : record.notify
                            ? t('publications.hintWillNotify')
                            : t('publications.hintWillBeQuiet')
                      }
                      onCheckedChange={(checked) => {
                        patch(record.id, { notify: checked });
                        toast({
                          variant: 'info',
                          title: checked
                            ? t('publications.notifiedTitle')
                            : t('publications.silentSetTitle'),
                          description: checked
                            ? t('publications.toggleOnBody', { title: record.title })
                            : t('publications.toggleOffBody', { title: record.title }),
                        });
                      }}
                    />
                  </div>
                </Panel>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={t('publications.confirmTitle')}
        confirmLabel={t('publications.confirmConfirm')}
        cancelLabel={t('publications.confirmCancel')}
        onConfirm={() => {
          if (confirming) publish(confirming);
          setConfirming(null);
        }}
      >
        {confirming ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-body">
              <strong className="font-medium text-heading">{confirming.title}</strong>{' '}
              {t('publications.confirmBody', {
                count: participationsOf(confirming.projectId).length,
                project: findProject(confirming.projectId)?.name ?? '',
              })}
            </p>

            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <dt className={LABEL}>{t('publications.confirmDiscloses')}</dt>
                <dd className="mt-0.5 font-mono text-sm text-heading">
                  {t('publications.confirmSpent', {
                    amount: formatMoney(confirming.spendToDate),
                  })}
                </dd>
              </div>
              <div>
                <dt className={LABEL}>{t('publications.confirmVariance')}</dt>
                <dd className="mt-0.5 font-mono text-sm text-heading">
                  {formatMoney(confirming.variance)}
                </dd>
              </div>
              <div>
                <dt className={LABEL}>{t('publications.confirmCarries')}</dt>
                <dd className="mt-0.5 font-mono text-sm text-heading">
                  {t('publications.countPhotos', { count: confirming.photos })},{' '}
                  {t('publications.countDocuments', { count: confirming.documents })}
                </dd>
              </div>
              <div>
                <dt className={LABEL}>{t('publications.confirmApprovedBy')}</dt>
                <dd className="mt-0.5 text-sm text-heading">
                  {t('publications.confirmApprovedOn', {
                    name: confirming.approvedBy ?? '',
                    date: formatDate(confirming.approvedAt),
                  })}
                </dd>
              </div>
            </dl>

            {confirming.notify ? (
              <InlineAlert
                tone="info"
                title={t('publications.notifiedTitle')}
                description={t('publications.notifiedBody')}
              />
            ) : (
              <InlineAlert
                tone="warning"
                title={t('publications.quietTitle')}
                description={t('publications.quietBody')}
              />
            )}
          </div>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
